import React from "react";
import PropTypes from 'prop-types';
import { renderRangeLabel,safeRenderValue } from "../../../../utils/reportUtils";
import "../../../../assets/scss/_marker-scale.scss";
import { useMediaQuery } from "react-responsive";

/**
 * BoundaryLabels component
 * This component renders labels for the boundaries of a marker scale.
 * It displays low, normal, and high boundary labels with optional styles
 * based on the provided props. Works for both standard markers and B12.
 * @returns {JSX.Element} The rendered boundary labels component.
 */

const BoundaryLabels = ({
  showLowBoundary,
  showHighBoundary,
  leftBarSize,
  barSize,
  normalBoundaryLabel,
  highBoundaryLabel,
  decision_type,
  type,
  lower_boundary,
  upper_boundary,
  boundary,
  code,
  scaleSegments // For B12 and other multi-segment scales
}) => {
  const isVitaminB12 = code === "B12A";
  const isMobile = useMediaQuery({ maxWidth: 767 });

  // For B12, use the segment information; otherwise use the bar size
  const displayLeftBarSize = isVitaminB12 && scaleSegments ? scaleSegments.leftSize : (leftBarSize || barSize);
  const displayBarSize = isVitaminB12 && scaleSegments ? scaleSegments.normalSize : barSize;

  return (
    <div className="markerScale__labels">
      
      {lower_boundary && !isVitaminB12  ? (
        <div 
          className="markerScale__label-range" 
          style={{
            position: "absolute", 
            left: `${(displayLeftBarSize)}%`, 
          }}
        >
          {safeRenderValue(lower_boundary)}
        </div>
      ) : null}

      {/* B12 Low-Normal segment */}
      {isVitaminB12 && scaleSegments && (
          <div 
            className="markerScale__label-range"
            style={{ position: "absolute", left: `${scaleSegments.lowNormalSize + (isMobile ? 1 : 2.2)}%` }}
          >
            {safeRenderValue(scaleSegments.lowBoundary)}
          </div>
      )}

      {!lower_boundary && !upper_boundary && (
        <div 
          className="markerScale__label-range" 
          style={{
            position: "absolute", 
            left: `${(displayLeftBarSize) - 0.5}%`, 
          }}
        >
          {renderRangeLabel(
            decision_type,
            type,
            lower_boundary,
            upper_boundary,
            boundary
          )}
        </div>
      )}

      {upper_boundary ? (
        <div 
          className="markerScale__label-range" 
          style={{
            position: "absolute", 
            left: `${isVitaminB12 && scaleSegments
              ? scaleSegments.lowSize + scaleSegments.lowNormalSize
              : (displayLeftBarSize + displayBarSize) -  0.2}%`, 
          }}
        >
          {isVitaminB12 && scaleSegments ? safeRenderValue(scaleSegments.lowNormalBoundary) : safeRenderValue(upper_boundary)}
        </div>
      ) : null}

      {isVitaminB12 && scaleSegments && (
        <div 
          className="markerScale__label-range"
          style={{ position: "absolute", left: `${scaleSegments.lowSize + scaleSegments.lowNormalSize + scaleSegments.normalSize }%` }}
        >
          {safeRenderValue(scaleSegments.normalBoundary)}
        </div>
      )}
    </div>
  );
};

BoundaryLabels.propTypes = {
  showLowBoundary: PropTypes.bool,
  showHighBoundary: PropTypes.bool,
  leftBarSize: PropTypes.number,
  barSize: PropTypes.number,
  normalBoundaryLabel: PropTypes.string,
  highBoundaryLabel: PropTypes.string,
  decision_type: PropTypes.string,
  type: PropTypes.string,
  lower_boundary: PropTypes.number,
  upper_boundary: PropTypes.number,
  boundary: PropTypes.oneOfType([PropTypes.number, PropTypes.object, PropTypes.array]),
  code: PropTypes.string,
  scaleSegments: PropTypes.object
};

BoundaryLabels.defaultProps = {
  showLowBoundary: true,
  showHighBoundary: true,
  normalBoundaryLabel: "Normal",
  highBoundaryLabel: "High"
};

export default BoundaryLabels;